/* eslint-disable max-lines, max-params, no-param-reassign */
import { api } from "jiffy-api";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { changeActiveCartName } from "components/jiffy/masthead-cart/masthead-cart";
import * as flag from "common/utils/flag";
import { isObject } from "common/utils/core";
import { EventBus, Events } from "jiffy-store";
import { scrollToTop } from "../helpers/sroll-to-alerts";

const initialState = {
  active: null,
  cartName: null,
  carts: [],
  defaultCartName: null,
  error: null,
  hidden: false,
  isAttaching: false,
  isCreating: false,
  isCustomerChoosing: false,
  isCustomerCreating: false,
  isDetaching: false,
  isLoaded: false,
  isLoading: false,
  isNamedCartsAvailable: false,
  isNamedCartsCustomerAvailable: false,
  isNamedCartsManagementAvailable: false,
  isNamedCartsVisible: false,
  isSwitching: false,
  isUpdating: false,
  notes: "",
  showAnnouncement: false,
  showDateAnnouncement: false,
  showDateTooltip: false,
  showDateTooltipOnce: flag.isOff("__JIFFY__VUE__MULTI_CARTS_DATE_TOOLTIP__"),
  updatedAt: null
};

// EventBus listener will be registered by the store after module initialization

const savedCartsModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  mutations: {
    init(state, payload) {
      const cartConfig = payload.cartConfig || payload.cart_config || {};
      const cartName = cartConfig.cartName || cartConfig.cart_name;
      const { notes } = cartConfig;
      const defaultCartName = cartConfig.defaultCartName || cartConfig.default_cart_name;
      const updatedAt = cartConfig.updatedAt || cartConfig.updated_at;
      const isNamedCartsAvailable =
        cartConfig.isNamedCartsAvailable || cartConfig.is_named_carts_available;
      const isNamedCartsManagementAvailable =
        cartConfig.isNamedCartsManagementAvailable ||
        cartConfig.is_named_carts_management_available;
      const isNamedCartsCustomerAvailable =
        cartConfig.isNamedCartsCustomerAvailable || cartConfig.is_named_carts_customer_available;
      const isNamedCartsVisible =
        cartConfig.isNamedCartsVisible || cartConfig.is_named_carts_visible;

      if (cartName !== undefined) {
        state.cartName = cartName;
      }
      if (notes !== undefined) {
        state.notes = notes;
      }
      if (defaultCartName !== undefined) {
        state.defaultCartName = defaultCartName;
      }
      if (updatedAt !== undefined) {
        state.updatedAt = updatedAt;
      }
      if (isNamedCartsAvailable !== undefined) {
        state.isNamedCartsAvailable = isNamedCartsAvailable;
      }
      if (isNamedCartsManagementAvailable !== undefined) {
        state.isNamedCartsManagementAvailable = isNamedCartsManagementAvailable;
      }
      if (isNamedCartsCustomerAvailable !== undefined) {
        state.isNamedCartsCustomerAvailable = isNamedCartsCustomerAvailable;
      }
      if (isNamedCartsVisible !== undefined) {
        state.isNamedCartsVisible = isNamedCartsVisible;
      }
    },
    setSavedCartsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setSavedCartsUpdating(state, isUpdating) {
      state.isUpdating = isUpdating;
    },
    setSavedCartsSwitching(state, isSwitching) {
      state.isSwitching = isSwitching;
    },
    setSavedCartsCreating(state, isCreating) {
      state.isCreating = isCreating;
    },
    setSavedCartsError(state, error) {
      state.error = error;
    },
    setSavedCarts(state, { carts, active }) {
      state.carts = carts;
      state.isLoaded = true;
      if (active) {
        state.active = active;
      }
    },
    setSavedCartsUpdatedAt(state, updatedAt) {
      state.updatedAt = updatedAt;
    },
    setSavedCartsAnnouncement(state, visible) {
      state.showAnnouncement = visible;
    },
    setSavedCartsDateAnnouncement(state, visible) {
      state.showDateAnnouncement = visible;
    },
    setSavedCartsDateTooltip(state, visible) {
      state.showDateTooltip = visible;
    },
    setSavedCartsDateTooltipOnce(state) {
      state.showDateTooltipOnce = false;
    },
    setSavedCartsName(state, name) {
      state.cartName = name;
    },
    setSavedCartsNotes(state, notes) {
      state.notes = notes;
    },
    setSavedCartsCustomerAttaching(state, isAttaching) {
      state.isAttaching = isAttaching;
    },
    setSavedCartsCustomer(state, customer) {
      state.customer = customer;
    },
    setSavedCartsCustomerDetaching(state, isAttaching) {
      state.isDetaching = isAttaching;
    },
    setSavedCartsCustomerCreating(state, isCustomerCreating) {
      state.isCustomerCreating = isCustomerCreating;
    },
    setSavedCartsCustomerChoosing(state, isCustomerChoosing) {
      state.isCustomerChoosing = isCustomerChoosing;
    }
  },

  actions: {
    async loadSavedCarts({ commit, getters, state }) {
      if (!getters.isNamedCartsAvailable) return;

      // Check if data is already loaded
      if (state.isLoaded && state.carts.length > 0) {
        return;
      }

      try {
        commit("setSavedCartsLoading", true);
        let carts;
        let active;

        if (getters.isNamedCartsManagementAvailable) {
          [{ carts }, active] = await Promise.all([
            api.jiffyshirts.savedCart.list(),
            api.jiffyshirts.savedCart.current()
          ]);
        } else {
          active = await api.jiffyshirts.savedCart.current();
          carts = [active];
        }
        commit("setSavedCarts", { carts, active });
        commit("setSavedCartsName", active?.cart_name);

        // Publish event for masthead dropdown to update
        EventBus.publish(Events.SAVED_CARTS_LOADED, null, { carts, activeCart: active });
      } catch (error) {
        commit("setSavedCartsError", error.message);
      } finally {
        commit("setSavedCartsLoading", false);
      }
    },

    async handleCartUpdate({ commit, getters }, cart) {
      try {
        commit("setSavedCartsError", null);
        commit("setSavedCartsUpdating", true);
        const id = cart.number || getters.savedCartsActive.number;
        await api.jiffyshirts.savedCart.update({ id }, { saved_carts: cart });
        commit("setSavedCartsUpdatedAt", new Date().toString());

        if (getters.isNamedCartsManagementAvailable) {
          const { carts } = await api.jiffyshirts.savedCart.list();
          commit("setSavedCarts", { carts });
          commit("setSavedCartsName", carts?.[0]?.cart_name);
        } else {
          const active = await api.jiffyshirts.savedCart.current();
          const carts = [active];

          commit("setSavedCarts", { carts });
          commit("setSavedCartsName", carts?.[0]?.cart_name);
        }
        commit("setSavedCartsUpdating", false);
      } catch (error) {
        const ignoreErrors = ["NetworkError when attempting to fetch resource."];
        if (!ignoreErrors.includes(error.message)) {
          commit("setSavedCartsError", error.message);
        }
      } finally {
        commit("setSavedCartsUpdating", false);
      }
    },
    async handleCartChange({ commit }, cart) {
      try {
        commit("setSavedCartsError", null);
        commit("setSavedCartsSwitching", true);
        await api.jiffyshirts.savedCart.switch({ id: cart.number });
        window.location.reload();
      } catch (error) {
        commit("setSavedCartsError", error.message);
        commit("setSavedCartsSwitching", false);
      }
    },
    async handleCartCreate({ commit }, cart) {
      try {
        commit("setSavedCartsError", null);
        commit("setSavedCartsCreating", true);
        await api.jiffyshirts.savedCart.create({ saved_carts: cart });
        window.location.reload();
      } catch (error) {
        commit("setSavedCartsError", error.message);
      } finally {
        commit("setSavedCartsCreating", false);
      }
    },
    hideSavedCartsAnnouncement({ commit }) {
      commit("setSavedCartsAnnouncement", false);
      flag.toggle("__JIFFY__VUE__MULTI_CARTS_ANNOUNCEMENT__");
    },
    hideSavedCartsDateAnnouncement({ commit }) {
      commit("setSavedCartsDateAnnouncement", false);
      flag.toggle("__JIFFY__VUE__MULTI_CARTS_DATE_ANNOUNCEMENT__");
    },
    toggleSavedCartsDateTooltip({ commit }, visible) {
      commit("setSavedCartsDateTooltip", visible);

      if (!visible) {
        commit("setSavedCartsDateTooltipOnce");
        flag.toggle("__JIFFY__VUE__MULTI_CARTS_DATE_TOOLTIP__");
      }
    },
    updateSavedCartsName({ commit }, name) {
      commit("setSavedCartsName", name);
      changeActiveCartName(name);
    },
    async attachCartCustomer({ commit, rootGetters }, customer) {
      try {
        commit("setSavedCartsError", null);
        commit("setSavedCartsCustomerAttaching", true);
        await api.consumers.attachToOrder(
          { orderId: rootGetters.orderNumber },
          { consumer_guid: customer.guid }
        );
        commit("setSavedCartsCustomer", customer);
      } catch (error) {
        commit("setSavedCartsError", error.message);
      } finally {
        commit("setSavedCartsCustomerAttaching", false);
      }
    },
    async detachCartCustomer({ commit, rootGetters }) {
      try {
        commit("setSavedCartsError", null);
        commit("setSavedCartsCustomerDetaching", true);
        await api.consumers.detachFromOrder({ orderId: rootGetters.orderNumber });
        commit("setSavedCartsCustomer", null);
      } catch (error) {
        commit("setSavedCartsError", error.message);
      } finally {
        commit("setSavedCartsCustomerDetaching", false);
      }
    },
    async createCartCustomer({ commit, rootGetters }, customer) {
      try {
        commit("setSavedCartsCustomerCreating", true);
        const { consumer, errors } = await api.consumers.create({
          consumer: customer,
          order_number: rootGetters.orderNumber
        });
        commit("setSavedCartsCustomerCreating", false);
        if (consumer) {
          commit("setSavedCartsCustomer", objNamesToCamelCase(consumer));
        }
        return { consumer, errors };
      } catch (error) {
        commit("setSavedCartsCustomerCreating", false);
        throw error;
      }
    },
    async checkCurrentCartActive({ commit, state }) {
      const response = await api.jiffyshirts.savedCart.current();
      const { active } = state;
      if (active === null) return;
      if (response?.number !== active.number) {
        scrollToTop();
        commit("setSavedCartsError", null);
        commit("setSavedCartsSwitching", true);
        commit(
          "pushNotification",
          {
            type: "warning",
            text: "Your cart has been changed. Updating your cart..."
          },
          { root: true }
        );

        window.location.reload();
      }
    },
    updateFromEventBus({ commit }, data) {
      if (data && data.carts && data.activeCart) {
        commit("setSavedCarts", { carts: data.carts, active: data.activeCart });
        commit("setSavedCartsName", data.activeCart?.cart_name);
      }
    }
  },

  getters: {
    savedCartsError: state => state.error,
    savedCartsLoading: state => state.isLoading,
    savedCartsUpdating: state => state.isUpdating,
    savedCartsSwitching: state => state.isSwitching,
    savedCartsCreating: state => state.isCreating,
    savedCartsList: state => state.carts,
    savedCartsActive: state => state.active,
    savedCartsHidden: state => state.hidden,
    savedCartsLoaded: state => state.isLoaded,
    savedCartsShowAnnouncement: state => state.showAnnouncement,
    savedCartsShowDateAnnouncement: state => state.showDateAnnouncement,
    savedCartsShowDateTooltip: state => state.showDateTooltip,
    savedCartsShowDateTooltipOnce: state => state.showDateTooltipOnce,
    savedCartsUpdatedAt: state => state.updatedAt,
    savedCartsName: state => state.cartName,
    savedCartsNotes: state => state.notes,
    currentSavedCart: state => ({
      cart_name: state.cartName,
      notes: state.notes,
      customer_purchase_order_number: ""
    }),
    defaultCartName: state => state.defaultCartName,
    isDefaultCartName:
      (state, getters) =>
      (cartName = getters.savedCartsName) => {
        const regex = new RegExp(`^${getters.defaultCartName}(\\s#\\d+){0,1}$`, "g");
        return regex.test(cartName);
      },
    savedCartsCustomer: state => {
      const customerData = state.customer;

      if (!isObject(customerData) || !Object.keys(customerData).length) {
        return null;
      }
      return customerData;
    },
    savedCartsIsCustomerAttaching: state => state.isAttaching,
    savedCartsIsCustomerDetaching: state => state.isDetaching,
    savedCartsIsCustomerCreating: state => state.isCustomerCreating,
    savedCartsIsCustomerChoosing: state => state.isCustomerChoosing,
    isNamedCartsAvailable: state => state.isNamedCartsAvailable,
    isNamedCartsManagementAvailable: state => state.isNamedCartsManagementAvailable,
    isNamedCartsCustomerAvailable: state => state.isNamedCartsCustomerAvailable,
    isNamedCartsVisible: state => state.isNamedCartsVisible
  }
};

export default savedCartsModule;
